import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  FolderOpen, 
  FileText, 
  MessageSquare, 
  Users, 
  LogOut,
  Plus,
  Trash2,
  Edit,
  Loader2,
  Eye,
  EyeOff
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "/assets/logo-circle.svg";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/admin/proyectos", icon: FolderOpen, label: "Proyectos" },
  { path: "/admin/blog", icon: FileText, label: "Blog" },
  { path: "/admin/contactos", icon: MessageSquare, label: "Contactos" },
  { path: "/admin/suscriptores", icon: Users, label: "Suscriptores" }
];

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({
    projects: 0,
    posts: 0,
    contacts: 0,
    subscribers: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const [projectsRes, postsRes, contactsRes, subscribersRes] = await Promise.all([
        fetch(`${API}/projects`),
        fetch(`${API}/blog?published=null`),
        fetch(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API}/subscribers`, { headers: { Authorization: `Bearer ${token}` }})
      ]);

      const projects = projectsRes.ok ? await projectsRes.json() : [];
      const posts = postsRes.ok ? await postsRes.json() : [];
      const contacts = contactsRes.ok ? await contactsRes.json() : [];
      const subscribers = subscribersRes.ok ? await subscribersRes.json() : [];

      setStats({
        projects: projects.length,
        posts: posts.length,
        contacts: contacts.length,
        subscribers: subscribers.length
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="text-3xl font-serif mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="p-6 bg-circulo-paper border border-white/10">
          <FolderOpen className="w-8 h-8 text-circulo-green mb-4" />
          <span className="text-3xl font-serif block mb-1">{stats.projects}</span>
          <span className="text-sm text-muted-foreground">Proyectos</span>
        </div>
        <div className="p-6 bg-circulo-paper border border-white/10">
          <FileText className="w-8 h-8 text-circulo-green mb-4" />
          <span className="text-3xl font-serif block mb-1">{stats.posts}</span>
          <span className="text-sm text-muted-foreground">Artículos</span>
        </div>
        <div className="p-6 bg-circulo-paper border border-white/10">
          <MessageSquare className="w-8 h-8 text-circulo-green mb-4" />
          <span className="text-3xl font-serif block mb-1">{stats.contacts}</span>
          <span className="text-sm text-muted-foreground">Contactos</span>
        </div>
        <div className="p-6 bg-circulo-paper border border-white/10">
          <Users className="w-8 h-8 text-circulo-sand mb-4" />
          <span className="text-3xl font-serif block mb-1">{stats.subscribers}</span>
          <span className="text-sm text-muted-foreground">Suscriptores</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="p-6 bg-circulo-paper border border-white/10">
          <h2 className="text-xl font-serif mb-4">Acciones Rápidas</h2>
          <div className="space-y-3">
            <Link to="/admin/proyectos" className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors">
              <Plus className="w-4 h-4 text-circulo-green" />
              <span>Agregar proyecto</span>
            </Link>
            <Link to="/admin/blog" className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors">
              <Plus className="w-4 h-4 text-circulo-green" />
              <span>Escribir artículo</span>
            </Link>
            <Link to="/admin/contactos" className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors">
              <MessageSquare className="w-4 h-4 text-circulo-sand" />
              <span>Ver contactos recientes</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Projects Management
const ProjectsManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    full_description: "",
    category: "",
    location: "",
    year: new Date().getFullYear().toString(),
    images: [],
    before_images: [],
    after_images: [],
    plants: [],
    featured: false
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`);
      if (response.ok) {
        setProjects(await response.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");
    
    try {
      const url = editingProject 
        ? `${API}/projects/${editingProject.id}`
        : `${API}/projects`;
      
      const response = await fetch(url, {
        method: editingProject ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingProject ? "Proyecto actualizado" : "Proyecto creado");
        setIsDialogOpen(false);
        setEditingProject(null);
        resetForm();
        fetchProjects();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/projects/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Proyecto eliminado");
        fetchProjects();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      full_description: project.full_description || "",
      category: project.category,
      location: project.location,
      year: project.year,
      images: project.images || [],
      before_images: project.before_images || [],
      after_images: project.after_images || [],
      plants: project.plants || [],
      featured: project.featured
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      full_description: "",
      category: "",
      location: "",
      year: new Date().getFullYear().toString(),
      images: [],
      before_images: [],
      after_images: [],
      plants: [],
      featured: false
    });
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-serif">Proyectos</h1>
        <button
          onClick={() => { resetForm(); setEditingProject(null); setIsDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-project-btn"
        >
          <Plus className="w-4 h-4" />
          Agregar proyecto
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-circulo-green" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay proyectos. Agrega el primero.
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((project) => (
            <div 
              key={project.id}
              className="p-4 bg-circulo-paper border border-white/10 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                {project.images?.[0] && (
                  <img src={project.images[0]} alt="" className="w-16 h-12 object-cover" />
                )}
                <div>
                  <h3 className="font-medium">{project.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {project.category} • {project.location} • {project.year}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(project)}
                  className="p-2 hover:bg-white/10 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteId(project.id)}
                  className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-circulo-paper border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              {editingProject ? "Editar proyecto" : "Nuevo proyecto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Título</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                required
                data-testid="project-title-input"
              />
            </div>
            <div>
              <Label>Descripción corta</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                rows={2}
                required
                data-testid="project-description-input"
              />
            </div>
            <div>
              <Label>Descripción completa</Label>
              <Textarea
                value={formData.full_description}
                onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                rows={5}
                placeholder="Descripción detallada del proyecto, contexto, retos, soluciones..."
                data-testid="project-full-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="mt-1 bg-transparent border-white/20" data-testid="project-category-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-circulo-paper border-white/10">
                    <SelectItem value="diseño_integral">Diseño Integral</SelectItem>
                    <SelectItem value="proyectos_estrategicos">Proyectos Estratégicos</SelectItem>
                    <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Año</Label>
                <Input
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  className="mt-1 bg-transparent border-white/20"
                  data-testid="project-year-input"
                />
              </div>
            </div>
            <div>
              <Label>Ubicación</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                placeholder="San José del Cabo"
                data-testid="project-location-input"
              />
            </div>

            {/* Galería principal */}
            <div className="border border-white/10 p-4 space-y-3">
              <Label className="text-circulo-sand text-xs uppercase tracking-wider">Galería de fotos (5-10 URLs, una por línea)</Label>
              <Textarea
                value={formData.images.join("\n")}
                onChange={(e) => setFormData({ ...formData, images: e.target.value.split("\n").filter(Boolean) })}
                className="mt-1 bg-transparent border-white/20 font-mono text-xs"
                rows={4}
                placeholder="https://imagen1.jpg&#10;https://imagen2.jpg&#10;https://imagen3.jpg"
                data-testid="project-images-input"
              />
              <p className="text-[10px] text-muted-foreground">{formData.images.length} imagen(es) cargada(s)</p>
            </div>

            {/* Antes y Después */}
            <div className="border border-white/10 p-4 space-y-3">
              <Label className="text-circulo-sand text-xs uppercase tracking-wider">Evolución: Antes y Después</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Fotos ANTES (URLs, una por línea)</Label>
                  <Textarea
                    value={formData.before_images.join("\n")}
                    onChange={(e) => setFormData({ ...formData, before_images: e.target.value.split("\n").filter(Boolean) })}
                    className="mt-1 bg-transparent border-white/20 font-mono text-xs"
                    rows={3}
                    placeholder="https://antes1.jpg&#10;https://antes2.jpg"
                    data-testid="project-before-images-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">{formData.before_images.length} foto(s)</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Fotos DESPUÉS (URLs, una por línea)</Label>
                  <Textarea
                    value={formData.after_images.join("\n")}
                    onChange={(e) => setFormData({ ...formData, after_images: e.target.value.split("\n").filter(Boolean) })}
                    className="mt-1 bg-transparent border-white/20 font-mono text-xs"
                    rows={3}
                    placeholder="https://despues1.jpg&#10;https://despues2.jpg"
                    data-testid="project-after-images-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">{formData.after_images.length} foto(s)</p>
                </div>
              </div>
            </div>

            {/* Paleta Vegetal */}
            <div className="border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-circulo-sand text-xs uppercase tracking-wider">Paleta Vegetal</Label>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    plants: [...formData.plants, { name: "", scientific_name: "", image_url: "" }]
                  })}
                  className="text-xs text-circulo-green hover:text-circulo-green-light flex items-center gap-1"
                  data-testid="add-plant-btn"
                >
                  <Plus className="w-3 h-3" /> Agregar planta
                </button>
              </div>
              {formData.plants.length === 0 && (
                <p className="text-xs text-muted-foreground py-2">No hay plantas. Agrega las especies utilizadas.</p>
              )}
              {formData.plants.map((plant, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-circulo-subtle/50 border border-white/5">
                  <div className="col-span-4">
                    <Label className="text-[10px] text-muted-foreground">Nombre</Label>
                    <Input
                      value={plant.name}
                      onChange={(e) => {
                        const updated = [...formData.plants];
                        updated[index] = { ...updated[index], name: e.target.value };
                        setFormData({ ...formData, plants: updated });
                      }}
                      className="mt-1 bg-transparent border-white/20 text-xs"
                      placeholder="Agave"
                      data-testid={`plant-name-${index}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Nombre científico</Label>
                    <Input
                      value={plant.scientific_name}
                      onChange={(e) => {
                        const updated = [...formData.plants];
                        updated[index] = { ...updated[index], scientific_name: e.target.value };
                        setFormData({ ...formData, plants: updated });
                      }}
                      className="mt-1 bg-transparent border-white/20 text-xs"
                      placeholder="Agave americana"
                      data-testid={`plant-scientific-${index}`}
                    />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px] text-muted-foreground">URL imagen</Label>
                    <Input
                      value={plant.image_url}
                      onChange={(e) => {
                        const updated = [...formData.plants];
                        updated[index] = { ...updated[index], image_url: e.target.value };
                        setFormData({ ...formData, plants: updated });
                      }}
                      className="mt-1 bg-transparent border-white/20 text-xs"
                      placeholder="https://..."
                      data-testid={`plant-image-${index}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.plants.filter((_, i) => i !== index);
                        setFormData({ ...formData, plants: updated });
                      }}
                      className="p-1 text-red-400 hover:text-red-300"
                      data-testid={`remove-plant-${index}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                data-testid="project-featured-checkbox"
              />
              <Label htmlFor="featured">Proyecto destacado</Label>
            </div>
            <button type="submit" className="w-full btn-primary" data-testid="project-submit-btn">
              {editingProject ? "Guardar cambios" : "Crear proyecto"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-circulo-paper border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Blog Manager
const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    cover_image: "",
    tags: [],
    published: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog?published=null`);
      if (response.ok) {
        setPosts(await response.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("admin_token");
    
    try {
      const url = editingPost 
        ? `${API}/blog/${editingPost.id}`
        : `${API}/blog`;
      
      const response = await fetch(url, {
        method: editingPost ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPost ? "Artículo actualizado" : "Artículo creado");
        setIsDialogOpen(false);
        setEditingPost(null);
        resetForm();
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/blog/${deleteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Artículo eliminado");
        fetchPosts();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      cover_image: post.cover_image,
      tags: post.tags || [],
      published: post.published
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      cover_image: "",
      tags: [],
      published: false
    });
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-serif">Blog</h1>
        <button
          onClick={() => { resetForm(); setEditingPost(null); setIsDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-post-btn"
        >
          <Plus className="w-4 h-4" />
          Nuevo artículo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-circulo-green" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay artículos. Escribe el primero.
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div 
              key={post.id}
              className="p-4 bg-circulo-paper border border-white/10 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                {post.cover_image && (
                  <img src={post.cover_image} alt="" className="w-16 h-12 object-cover" />
                )}
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    {post.title}
                    {post.published ? (
                      <Eye className="w-4 h-4 text-circulo-green" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    /{post.slug}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(post)}
                  className="p-2 hover:bg-white/10 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeleteId(post.id)}
                  className="p-2 hover:bg-red-500/20 text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Blog Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-circulo-paper border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              {editingPost ? "Editar artículo" : "Nuevo artículo"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Título</Label>
              <Input
                value={formData.title}
                onChange={(e) => {
                  setFormData({ 
                    ...formData, 
                    title: e.target.value,
                    slug: editingPost ? formData.slug : generateSlug(e.target.value)
                  });
                }}
                className="mt-1 bg-transparent border-white/20"
                required
              />
            </div>
            <div>
              <Label>Slug (URL)</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                required
              />
            </div>
            <div>
              <Label>Extracto</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                rows={2}
                required
              />
            </div>
            <div>
              <Label>Contenido (Markdown)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="mt-1 bg-transparent border-white/20 font-mono text-sm"
                rows={10}
                placeholder="## Título&#10;&#10;Contenido del artículo..."
              />
            </div>
            <div>
              <Label>Imagen de portada (URL)</Label>
              <Input
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                className="mt-1 bg-transparent border-white/20"
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Tags (separados por coma)</Label>
              <Input
                value={formData.tags.join(", ")}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })}
                className="mt-1 bg-transparent border-white/20"
                placeholder="Diseño, Los Cabos, Vegetación"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              />
              <Label htmlFor="published">Publicado</Label>
            </div>
            <button type="submit" className="w-full btn-primary">
              {editingPost ? "Guardar cambios" : "Crear artículo"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-circulo-paper border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Contacts Manager
const ContactsManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setContacts(await response.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const token = localStorage.getItem("admin_token");
    try {
      await fetch(`${API}/contacts/${id}/status?status=${status}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
      toast.success("Estado actualizado");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div data-testid="admin-contacts">
      <h1 className="text-3xl font-serif mb-8">Contactos</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-circulo-green" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay contactos recibidos.
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div 
              key={contact.id}
              className="p-6 bg-circulo-paper border border-white/10"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-medium text-lg">{contact.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {contact.email} • {contact.phone}
                  </p>
                </div>
                <Select
                  value={contact.status}
                  onValueChange={(v) => updateStatus(contact.id, v)}
                >
                  <SelectTrigger className="w-32 bg-transparent border-white/20 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-circulo-paper border-white/10">
                    <SelectItem value="nuevo">Nuevo</SelectItem>
                    <SelectItem value="contactado">Contactado</SelectItem>
                    <SelectItem value="en_proceso">En proceso</SelectItem>
                    <SelectItem value="cerrado">Cerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <span className="text-muted-foreground">Servicio: </span>
                  {contact.service_type}
                </div>
                <div>
                  <span className="text-muted-foreground">Propiedad: </span>
                  {contact.property_type || "No especificado"}
                </div>
                <div>
                  <span className="text-muted-foreground">Ubicación: </span>
                  {contact.location || "No especificada"}
                </div>
                <div>
                  <span className="text-muted-foreground">Fecha: </span>
                  {formatDate(contact.created_at)}
                </div>
              </div>
              {contact.message && (
                <div className="p-4 bg-circulo-subtle border border-white/5">
                  <p className="text-sm text-muted-foreground">{contact.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Subscribers Manager
const SubscribersManager = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setSubscribers(await response.json());
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div data-testid="admin-subscribers">
      <h1 className="text-3xl font-serif mb-8">Suscriptores</h1>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-circulo-green" />
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No hay suscriptores aún.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Cliente</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Plan</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Monto</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Fecha</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Estado</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub) => (
                <tr key={sub.id} className="border-b border-white/5">
                  <td className="py-4 px-4">
                    <div>
                      <span className="block">{sub.customer_name}</span>
                      <span className="text-sm text-muted-foreground">{sub.customer_email}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">{sub.plan_name}</td>
                  <td className="py-4 px-4">${sub.amount} {sub.currency?.toUpperCase()}</td>
                  <td className="py-4 px-4">{formatDate(sub.created_at)}</td>
                  <td className="py-4 px-4">
                    <span className="px-2 py-1 text-xs bg-circulo-green/20 text-circulo-green">
                      {sub.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, admin } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-circulo-black flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar fixed left-0 top-0 h-full w-64 p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-3 mb-12">
          <img src={LOGO_URL} alt="Circle" className="h-10 w-10 rounded-full object-cover invert" />
          <div>
            <span className="block text-sm font-serif">EL CÍRCULO</span>
            <span className="text-[10px] tracking-wider text-muted-foreground">ADMIN</span>
          </div>
        </Link>

        <nav className="flex-grow space-y-2">
          {navItems.map((item) => {
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${isActive ? "active" : ""}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-white/10 pt-6">
          {admin && (
            <div className="mb-4 px-3">
              <span className="text-sm block">{admin.name}</span>
              <span className="text-xs text-muted-foreground">{admin.email}</span>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-red-400 hover:text-red-300"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow ml-64 p-8">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="proyectos" element={<ProjectsManager />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="contactos" element={<ContactsManager />} />
          <Route path="suscriptores" element={<SubscribersManager />} />
        </Routes>
      </main>
    </div>
  );
}
